import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  Lightbulb, BookOpen, StickyNote, Plus, Loader2, 
  Calendar, Layers, ArrowRight, Sparkles, CheckCircle2,
  Circle, Trash2, Edit, Pin, Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';

const DashboardThinkLab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Think Lab state from localStorage
  const [journalEntries, setJournalEntries] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [pinnedResources, setPinnedResources] = useState([]);
  const [newJournalEntry, setNewJournalEntry] = useState('');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newResourceUrl, setNewResourceUrl] = useState('');
  const [newResourceTitle, setNewResourceTitle] = useState('');
  const [showAddJournal, setShowAddJournal] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddResource, setShowAddResource] = useState(false);

  useEffect(() => {
    loadThinkLabData();
  }, []);

  const loadThinkLabData = () => {
    try {
      // Load from localStorage
      const savedJournal = localStorage.getItem('thinklab_journal');
      const savedTasks = localStorage.getItem('thinklab_tasks');
      const savedResources = localStorage.getItem('thinklab_resources');
      
      if (savedJournal) setJournalEntries(JSON.parse(savedJournal));
      if (savedTasks) setTasks(JSON.parse(savedTasks));
      if (savedResources) setPinnedResources(JSON.parse(savedResources));
    } catch (error) {
      console.error('Error loading Think Lab data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveJournalEntries = (entries) => {
    setJournalEntries(entries);
    localStorage.setItem('thinklab_journal', JSON.stringify(entries));
  };

  const saveTasks = (updatedTasks) => {
    setTasks(updatedTasks);
    localStorage.setItem('thinklab_tasks', JSON.stringify(updatedTasks));
  };

  const saveResources = (resources) => {
    setPinnedResources(resources);
    localStorage.setItem('thinklab_resources', JSON.stringify(resources));
  };

  const handleAddJournal = () => {
    if (!newJournalEntry.trim()) return;
    
    const entry = {
      id: Date.now(),
      content: newJournalEntry,
      timestamp: new Date().toISOString()
    };
    
    saveJournalEntries([entry, ...journalEntries]);
    setNewJournalEntry('');
    setShowAddJournal(false);
    toast({ title: "Journal entry added!" });
  };

  const handleAddTask = () => {
    if (!newTaskTitle.trim()) return;
    
    const task = {
      id: Date.now(),
      title: newTaskTitle,
      completed: false,
      createdAt: new Date().toISOString()
    };
    
    saveTasks([...tasks, task]);
    setNewTaskTitle('');
    setShowAddTask(false);
    toast({ title: "Task added to list!" });
  };

  const handleAddResource = () => {
    if (!newResourceUrl.trim() || !newResourceTitle.trim()) {
      toast({ title: "Please fill in all fields", variant: "destructive" });
      return;
    }
    
    const resource = {
      id: Date.now(),
      url: newResourceUrl,
      title: newResourceTitle,
      pinnedAt: new Date().toISOString()
    };
    
    saveResources([...pinnedResources, resource]);
    setNewResourceUrl('');
    setNewResourceTitle('');
    setShowAddResource(false);
    toast({ title: "Resource pinned!" });
  };

  const handleToggleTask = (taskId) => {
    const updated = tasks.map(t => 
      t.id === taskId ? { ...t, completed: !t.completed } : t
    );
    saveTasks(updated);
  };

  const handleDeleteJournal = (id) => {
    saveJournalEntries(journalEntries.filter(e => e.id !== id));
    toast({ title: "Journal entry deleted" });
  };

  const handleDeleteTask = (id) => {
    saveTasks(tasks.filter(t => t.id !== id));
    toast({ title: "Task deleted" });
  };

  const handleDeleteResource = (id) => {
    saveResources(pinnedResources.filter(r => r.id !== id));
    toast({ title: "Resource unpinned" });
  };

  if (loading) {
    return (
      <Card className="border-2 border-purple-200">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const totalItems = journalEntries.length + tasks.length + pinnedResources.length;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Sparkles className="w-6 h-6 text-purple-600" />
                Think Lab Workspace
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1">
                Your personal ideation and productivity space
              </p>
            </div>
            <Button
              onClick={() => navigate('/session/new')}
              className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              New IF Session
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-3 mb-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                <span className="font-semibold">Journal</span>
              </div>
              <div className="text-3xl font-bold text-blue-600">{journalEntries.length}</div>
              <p className="text-xs text-slate-500 mt-1">Entries logged</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="font-semibold">Tasks</span>
              </div>
              <div className="text-3xl font-bold text-green-600">
                {tasks.filter(t => !t.completed).length}/{tasks.length}
              </div>
              <p className="text-xs text-slate-500 mt-1">Active tasks</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg border">
              <div className="flex items-center gap-3 mb-2">
                <Pin className="w-5 h-5 text-orange-600" />
                <span className="font-semibold">Resources</span>
              </div>
              <div className="text-3xl font-bold text-orange-600">{pinnedResources.length}</div>
              <p className="text-xs text-slate-500 mt-1">Pinned references</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Journal Entries */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                Journal
              </CardTitle>
              <Button size="sm" onClick={() => setShowAddJournal(!showAddJournal)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Entry
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {showAddJournal && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <Textarea
                  value={newJournalEntry}
                  onChange={(e) => setNewJournalEntry(e.target.value)}
                  placeholder="Write your thoughts, reflections, or observations..."
                  rows={3}
                  className="mb-2"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddJournal}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddJournal(false)}>Cancel</Button>
                </div>
              </div>
            )}
            
            {journalEntries.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-sm">No journal entries yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {journalEntries.map((entry) => (
                  <div key={entry.id} className="p-3 bg-slate-50 rounded-lg border">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs text-slate-500">
                        {new Date(entry.timestamp).toLocaleString()}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteJournal(entry.id)}
                      >
                        <Trash2 className="w-3 h-3 text-red-600" />
                      </Button>
                    </div>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{entry.content}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tasks / To-Do Lists */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                To-Do List
              </CardTitle>
              <Button size="sm" onClick={() => setShowAddTask(!showAddTask)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Task
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {showAddTask && (
              <div className="mb-4 p-3 bg-green-50 rounded-lg">
                <Input
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="Task title..."
                  className="mb-2"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddTask}>Add</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddTask(false)}>Cancel</Button>
                </div>
              </div>
            )}
            
            {tasks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle2 className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-sm">No tasks yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className={`p-3 rounded-lg border flex items-center justify-between ${
                      task.completed ? 'bg-green-50 border-green-200' : 'bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <button onClick={() => handleToggleTask(task.id)}>
                        {task.completed ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      <span className={task.completed ? 'line-through text-slate-500' : 'text-slate-800'}>
                        {task.title}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteTask(task.id)}
                    >
                      <Trash2 className="w-3 h-3 text-red-600" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pinned Resources */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Pin className="w-5 h-5 text-orange-600" />
              Pinned Resources
            </CardTitle>
            <Button size="sm" onClick={() => setShowAddResource(!showAddResource)}>
              <Plus className="w-4 h-4 mr-1" />
              Pin Resource
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showAddResource && (
            <div className="mb-4 p-4 bg-orange-50 rounded-lg">
              <Input
                value={newResourceTitle}
                onChange={(e) => setNewResourceTitle(e.target.value)}
                placeholder="Resource title..."
                className="mb-2"
              />
              <Input
                value={newResourceUrl}
                onChange={(e) => setNewResourceUrl(e.target.value)}
                placeholder="URL..."
                className="mb-2"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddResource}>Pin</Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddResource(false)}>Cancel</Button>
              </div>
            </div>
          )}
          
          {pinnedResources.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Pin className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-sm">No resources pinned yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-4">
              {pinnedResources.map((resource) => (
                <div
                  key={resource.id}
                  className="p-4 bg-slate-50 rounded-lg border hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-2">
                    <Pin className="w-4 h-4 text-orange-600" />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteResource(resource.id)}
                    >
                      <Trash2 className="w-3 h-3 text-red-600" />
                    </Button>
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">{resource.title}</h4>
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline truncate block"
                  >
                    {resource.url}
                  </a>
                  <p className="text-xs text-slate-500 mt-2">
                    Pinned {new Date(resource.pinnedAt).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Empty State */}
      {totalItems === 0 && (
        <Card className="border-2 border-dashed border-purple-200">
          <CardContent className="text-center py-12">
            <Lightbulb className="w-16 h-16 text-purple-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Start Using Think Lab</h3>
            <p className="text-slate-600 mb-6">
              Add journal entries, create tasks, and pin resources to organize your innovation work
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardThinkLab;
